'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section className="relative min-h-screen pt-24 sm:pt-28 md:pt-32 pb-12 md:pb-16 px-4 sm:px-6 md:px-12 max-w-8xl mx-auto">
      {/* Decorative Vertical Grid Lines */}
      {/* removed */}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-end relative z-10">
        {/* Left Content */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="mb-8 md:mb-12 animate-fade-in">
            <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 border border-primary/30 rounded-full text-xs uppercase tracking-widest text-primary mb-4 sm:mb-6">
              International Law Firm
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-light leading-[0.95] tracking-tight text-foreground mb-6 md:mb-8">
              International Legal{' '}
              <span className="italic text-primary">Excellence</span>
              <br />
              Across 12 Jurisdictions
            </h1>
            <p className="max-w-md text-base sm:text-lg text-muted-foreground leading-relaxed border-l-2 border-secondary pl-4 sm:pl-6">
              Expert counsel in corporate, IP, and tax law—with attorneys on the ground where you do business.
            </p>
          </div>

          {/* Consultation Card (Glassmorphism) */}
          <div className="glass-card rounded-2xl p-5 sm:p-6 max-w-md animate-fade-in-delay-1 border border-primary/10 transition-all duration-300 hover:shadow-2xl hover:border-primary/30 hover:scale-[1.02]">
            <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">
              Schedule a Consultation
            </h3>
            <div className="space-y-3 mb-4">
              <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between border border-primary/10 transition-all duration-200 hover:bg-muted hover:border-primary/20">
                <span className="text-sm font-medium text-foreground">Select Practice Area</span>
                <Icon name="ChevronDownIcon" size={16} className="text-muted-foreground" />
              </div>
              <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between border border-primary/10 transition-all duration-200 hover:bg-muted hover:border-primary/20">
                <span className="text-sm font-medium text-foreground">Preferred Country</span>
                <Icon name="ChevronDownIcon" size={16} className="text-muted-foreground" />
              </div>
            </div>
            <Link
              href="/contact"
              className="w-full bg-secondary text-secondary-foreground h-12 rounded-lg hover:bg-secondary/90 transition-all duration-300 flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-wider shadow-lg hover:shadow-xl hover:scale-105 group">
              
              <span>Book Consultation</span>
              <Icon name="ArrowRightIcon" size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* Right Image (Arch Shape) */}
        <div className="lg:col-span-7 h-[50vh] sm:h-[60vh] lg:h-[85vh] relative order-1 lg:order-2">
          <div className="absolute inset-0 arch-shape overflow-hidden shadow-2xl">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1713d6017-1772212128354.png"
              alt="Modern international law office with glass windows and professional workspace"
              className="w-full h-full object-cover hover-scale" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />

            {/* Floating Badge (Rotating) */}
            {isHydrated &&
            <div className="absolute top-8 sm:top-12 right-8 sm:right-12 hidden md:block pointer-events-none">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
                  <svg className="animate-rotate-slow w-full h-full" viewBox="0 0 100 100">
                    <path
                    id="circlePath"
                    d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
                    fill="transparent" />
                  
                    <text
                    fontSize="10"
                    fontFamily="Inter"
                    fontWeight="600"
                    letterSpacing="2"
                    fill="white">
                    
                      <textPath href="#circlePath" startOffset="0%">
                        CYFIRE • INTERNATIONAL • LEGAL •
                      </textPath>
                    </text>
                  </svg>
                  <Icon name="ScaleIcon" size={20} className="absolute text-white" />
                </div>
              </div>
            }

            {/* Floating Info Card */}
            <div className="absolute bottom-6 sm:bottom-8 right-6 sm:right-8 glass-card p-3 sm:p-4 rounded-xl text-white max-w-[180px] sm:max-w-[220px] hidden md:block transition-all duration-300 hover:scale-105 hover:shadow-2xl">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="GlobeAltIcon" size={18} />
                <span className="text-sm font-semibold">12 Countries</span>
              </div>
              <p className="text-xs leading-relaxed opacity-90">
                Physical presence with licensed attorneys in each jurisdiction.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}